import { Button, If } from "@hex-insights/core";
import { AddSubFormButtonRenderProps, Form, RadioField, SubFormField, SubFormRenderProps } from "@hex-insights/forms";
import { GuardianFormValues, PersonFormValues, RelationshipFormValues } from "../../FormUtilities";
import { PersonForm, PersonSelectField } from "../Person";
import { RelationshipForm } from "../Relationship";
import { BaseFieldProps } from "../shared";

/**
 * Generic props for fields of the Guardian model.
 */
type FieldProps<K extends keyof GuardianFormValues.Base = keyof GuardianFormValues.Base> = BaseFieldProps<
	Pick<GuardianFormValues.Base, K>
>;

/**
 * Generic props for fields of the Guardian model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof GuardianFormValues.Create = keyof GuardianFormValues.Create> = BaseFieldProps<
	Pick<GuardianFormValues.Create, K>
>;

/**
 * Generic props for fields of the Guardian model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof GuardianFormValues.Detail = keyof GuardianFormValues.Detail> = BaseFieldProps<
	Pick<GuardianFormValues.Detail, K>
>;

/**
 * Field component for the `relationshipStatus` field of the Guardian model.
 */
export function RelationshipStatus({ formState, ...props }: FieldProps<"relationshipStatus">) {
	return (
		<RadioField
			formState={formState}
			name="relationshipStatus"
			options={GuardianFormValues.relationshipStatusOptions}
			blankValue={null}
			optional
			{...props}
		/>
	);
}

export namespace Person {
	export type SelectProps = DetailFieldProps<"personID"> &
		Omit<PersonSelectField.SelectProps<GuardianFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `person` edge of the Guardian model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PersonSelectField.Select formState={formState} name="personID" {...props} />;
	}

	/**
	 * SubForm field component for the `person` edge of the TestModel model.
	 */
	export function SubForm({ formState, noHeader = true, style, ...props }: CreateFieldProps<"person">) {
		return (
			<SubFormField
				formState={formState}
				name="person"
				blankItem={PersonFormValues.blankItemInGuardian}
				minItems={1}
				maxItems={1}
				noHeader={noHeader}
				style={{ "--sub-form-field__value---padding": 0, width: "100%", ...style } as React.CSSProperties}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<PersonFormValues.CreateInGuardian>) {
		return (
			<Form.Column>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Person
					</Button>
				</If>
				<PersonForm.CreateFieldsInGuardian formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<Pick<GuardianFormValues.Create, "person">, PersonFormValues.CreateInGuardian>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Person
				</Button>
			</If>
		);
	};
}

export namespace Relationships {
	/**
	 * SubForm field component for the `relationships` edge of the TestModel model.
	 */
	export function SubForm({ formState, label = "Student Relationships", ...props }: CreateFieldProps<"relationships">) {
		return (
			<SubFormField
				formState={formState}
				name="relationships"
				label={label}
				blankItem={RelationshipFormValues.blankItemInGuardian}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<RelationshipFormValues.CreateInGuardian>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Relationship
				</Button>
				<RelationshipForm.CreateFieldsInGuardian formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<GuardianFormValues.Create, "relationships">,
		RelationshipFormValues.CreateInGuardian
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Relationship
			</Button>
		);
	};
}
